import { useState, useEffect } from "react";
import {
  Mail,
  Phone,
  Globe,
  Twitter,
  Instagram,
  User,
  Users,
  Megaphone,
  Hash,
} from "lucide-react";
import Swal from "sweetalert2";
import { GetTenantDetail, SaveTenantDetail, TenantDetailData } from "../../../services/outletDetail";

const INITIAL_VALUES: TenantDetailData = {
  TenantDetailId: 0,
  TenantId: 0,
  PhoneNumber: "",
  Email: "",
  Website: "",
  Twitter: "",
  Instagram: "",
  OwnerName: "",
  OwnerPhoneNumber: "",
  OwnerEmail: "",
  StoreManagerName: "",
  StoreManagerPhoneNumber: "",
  StoreManagerEmail: "",
  MarketingName: "",
  MarketingPhoneNumber: "",
  MarketingEmail: "",
  MaxCapacity: 0,
  IsActive: true,
  Status: 1,
};

interface InputFieldProps {
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  icon: React.ReactNode;
  required?: boolean;
}

const InputField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  icon,
  required,
}: InputFieldProps) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100">
        {icon}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-10 py-2.5 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
      />
    </div>
  </div>
);

const SectionTitle = ({ title }: { title: string }) => (
  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-6 pb-2 border-b border-gray-100 dark:border-gray-800">
    {title}
  </h2>
);

export default function TenantDetailForm({ tenantId }: { tenantId: number | null }) {
  const [formData, setFormData] = useState<TenantDetailData>(INITIAL_VALUES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTenantDetail = async (detailId: number, tid: number) => {
    setLoading(true);
    try {
      const response = await GetTenantDetail(detailId, tid, 0, 0);
      if (response?.Code === 0 && response.tenantDetailDatas.length > 0) {
        const detail = response.tenantDetailDatas[0];
        setFormData({
          TenantDetailId: detail.TenantDetailId,
          TenantId: detail.TenantId,
          PhoneNumber: detail.PhoneNumber ?? "",
          Email: detail.Email ?? "",
          Website: detail.Website ?? "",
          Twitter: detail.Twitter ?? "",
          Instagram: detail.Instagram ?? "",
          OwnerName: detail.OwnerName ?? "",
          OwnerPhoneNumber: detail.OwnerPhoneNumber ?? "",
          OwnerEmail: detail.OwnerEmail ?? "",
          StoreManagerName: detail.StoreManagerName ?? "",
          StoreManagerPhoneNumber: detail.StoreManagerPhoneNumber ?? "",
          StoreManagerEmail: detail.StoreManagerEmail ?? "",
          MarketingName: detail.MarketingName ?? "",
          MarketingPhoneNumber: detail.MarketingPhoneNumber ?? "",
          MarketingEmail: detail.MarketingEmail ?? "",
          MaxCapacity: detail.MaxCapacity ?? 0,
          IsActive: detail.IsActive ?? true,
          Status: detail.Status ?? 1,
        });
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to fetch tenant detail data",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.PhoneNumber) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Phone Number is required",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setSaving(true);
    try {
      const payload: TenantDetailData = {
        ...formData,
        TenantDetailId: 0,
        TenantId: tenantId ?? formData.TenantId,
      };

      const response = await SaveTenantDetail(payload);

      if (response?.Code === 0) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Tenant detail saved successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: response?.Message ?? "Failed to save tenant detail",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to save tenant detail",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (tenantId !== null && tenantId !== undefined) {
      fetchTenantDetail(0, tenantId);
    } else {
      setFormData(INITIAL_VALUES);
    }
  }, [tenantId]);
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "Status") {
      const statusValue = Number(value);
      setFormData((prev) => ({
        ...prev,
        Status: statusValue,
        IsActive: statusValue === 1,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: name === "MaxCapacity" ? Number(value) : value,
    }));
  };

  const handleDiscard = () => {
    setFormData(INITIAL_VALUES);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
          Loading tenant detail data...
        </p>
      </div>
    );
  }

  return (
    <div className="lg:p-6">
      <div className="max-w-5xl mx-auto p-6 sm:p-8">

          <div className="mb-8">
            <SectionTitle title="General Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Phone Number"
                name="PhoneNumber"
                value={formData.PhoneNumber}
                onChange={handleChange}
                placeholder="08123456789"
                type="tel"
                icon={<Phone className="w-4 h-4" />}
                required
              />
              <InputField
                label="Email"
                name="Email"
                value={formData.Email}
                onChange={handleChange}
                placeholder="tenant@email.com"
                type="email"
                icon={<Mail className="w-4 h-4" />}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Website"
                name="Website"
                value={formData.Website}
                onChange={handleChange}
                placeholder="https://example.com"
                icon={<Globe className="w-4 h-4" />}
              />
              <InputField
                label="Max Capacity"
                name="MaxCapacity"
                value={formData.MaxCapacity === 0 ? "" : formData.MaxCapacity}
                onChange={handleChange}
                placeholder="0"
                type="number"
                icon={<Hash className="w-4 h-4" />}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Twitter"
                name="Twitter"
                value={formData.Twitter}
                onChange={handleChange}
                placeholder="@twitter_handle"
                icon={<Twitter className="w-4 h-4" />}
              />
              <InputField
                label="Instagram"
                name="Instagram"
                value={formData.Instagram}
                onChange={handleChange}
                placeholder="@instagram_handle"
                icon={<Instagram className="w-4 h-4" />}
              />
            </div>
          </div>

          <div className="mb-8">
            <SectionTitle title="Owner Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Owner Name"
                name="OwnerName"
                value={formData.OwnerName}
                onChange={handleChange}
                placeholder="Enter owner name"
                icon={<User className="w-4 h-4" />}
              />
              <InputField
                label="Owner Phone Number"
                name="OwnerPhoneNumber"
                value={formData.OwnerPhoneNumber}
                onChange={handleChange}
                placeholder="08123456789"
                type="tel"
                icon={<Phone className="w-4 h-4" />}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Owner Email"
                name="OwnerEmail"
                value={formData.OwnerEmail}
                onChange={handleChange}
                placeholder="owner@email.com"
                type="email"
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
          </div>

          <div className="mb-8">
            <SectionTitle title="Store Manager Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Store Manager Name"
                name="StoreManagerName"
                value={formData.StoreManagerName}
                onChange={handleChange}
                placeholder="Enter store manager name"
                icon={<Users className="w-4 h-4" />}
              />
              <InputField
                label="Store Manager Phone Number"
                name="StoreManagerPhoneNumber"
                value={formData.StoreManagerPhoneNumber}
                onChange={handleChange}
                placeholder="08123456789"
                type="tel"
                icon={<Phone className="w-4 h-4" />}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Store Manager Email"
                name="StoreManagerEmail"
                value={formData.StoreManagerEmail}
                onChange={handleChange}
                placeholder="manager@email.com"
                type="email"
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
          </div>

          <div className="mb-8">
            <SectionTitle title="Marketing Information" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Marketing Name"
                name="MarketingName"
                value={formData.MarketingName}
                onChange={handleChange}
                placeholder="Enter marketing name"
                icon={<Megaphone className="w-4 h-4" />}
              />
              <InputField
                label="Marketing Phone Number"
                name="MarketingPhoneNumber"
                value={formData.MarketingPhoneNumber}
                onChange={handleChange}
                placeholder="08123456789"
                type="tel"
                icon={<Phone className="w-4 h-4" />}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Marketing Email"
                name="MarketingEmail"
                value={formData.MarketingEmail}
                onChange={handleChange}
                placeholder="marketing@email.com"
                type="email"
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={handleDiscard}
              className="px-8 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-all duration-200"
            >
              Discard Draft
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
      </div>
    </div>
  );
}